import { trpc } from "@/lib/trpc";
import { Shield, Lock, ArrowRight, Loader2, Users, Dumbbell, BookOpen, MessageSquare } from "lucide-react";
import { useLocation, useSearchParams } from "wouter";
import { useEffect, useRef, useState } from "react";
import { AccountSupportThread } from "@/components/AccountSupportThread";
import { Redirect } from "wouter";
import { Capacitor } from "@capacitor/core";
import { setNativeSessionToken } from "@/lib/apiBase";

interface SubscriptionGateProps {
  children: React.ReactNode;
  recoveryChildren?: React.ReactNode;
}

/**
 * Wraps client routes. If the user has no active subscription,
 * shows a locked state with a CTA to go to /client/billing.
 *
 * Bypassed for:
 * - billing recovery (without the protected client shell)
 * - explicit enrollment routes for clients who are not inactive
 * - admin and coach roles (owner always has full access, no paywall ever)
 */
export function SubscriptionGate({ children, recoveryChildren }: SubscriptionGateProps) {
  const [location, navigate] = useLocation();
  const { data: me, isLoading: meLoading } = trpc.auth.me.useQuery();

  // Still loading auth — show spinner, don't flash the paywall
  if (meLoading || !me) {
    return (
      <div data-contrast="dark" className="premium-dark-surface flex items-center justify-center min-h-[60dvh]" style={{ background: '#0e0e0e' }}>
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--primary)' }} />
      </div>
    );
  }

  // Admin and coach accounts always pass through — no paywall, no tier blocks
  if (me.role === 'admin' || me.role === 'coach') {
    return <>{children}</>;
  }

  // For regular clients, check subscription status
  return <ClientAccessCheck key={me.id} navigate={navigate} recoveryChildren={recoveryChildren}>{children}</ClientAccessCheck>;
}

// This boundary runs before the client shell mounts. A failed access check must
// never fall through to protected page hooks, cached workouts, or the DM inbox.
function ClientAccessCheck({ children, navigate, recoveryChildren }: {
  children: React.ReactNode;
  recoveryChildren?: React.ReactNode;
  navigate: (path: string) => void;
}) {
  const [location] = useLocation();
  const access = trpc.accountSupport.getAccess.useQuery(undefined, {
    retry: false, refetchInterval: 30_000, staleTime: 0, refetchOnMount: 'always',
  });
  const utils = trpc.useUtils();
  const bootstrapAttempted = useRef(false);
  const [bootstrapping, setBootstrapping] = useState(false);
  const [setupPath, setSetupPath] = useState('/client/onboarding');
  const isBilling = location === '/client/billing' || location.startsWith('/client/billing/');
  const enrollmentPaths = ['/onboarding', '/client/onboarding', '/client/standard-activation', '/client/standard-start'];
  const isEnrollment = enrollmentPaths.some(path => location === path || location.startsWith(`${path}/`));
  useEffect(() => {
    if (!access.data?.enrollmentPending || access.data.reason === 'inactive' || access.isError || isBilling || isEnrollment || bootstrapAttempted.current) return;
    bootstrapAttempted.current = true;
    setBootstrapping(true);
    // Legacy comped invites and eligible Standard purchases need their existing
    // enrollment bootstrap once before the definitive access check. The server
    // permits this only for verified pending enrollment, never canceled clients.
    void Promise.all([utils.billing.getSubscription.fetch(), utils.standardMembership.getMyState.fetch()])
      .then(([, standard]) => { if (standard?.canActivate) setSetupPath('/client/standard-activation'); })
      .catch(() => undefined)
      .then(() => access.refetch())
      .finally(() => setBootstrapping(false));
    // One bounded bootstrap per account boundary, not on every access poll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [access.data?.enrollmentPending, access.data?.reason, access.isError, isBilling, isEnrollment]);
  if (access.isLoading || (!access.isFetchedAfterMount && !access.isError)) return <div role="status" aria-label="Checking account access" data-contrast="dark" className="premium-dark-surface flex min-h-[100dvh] items-center justify-center bg-[#0e0e0e]"><Loader2 className="size-6 animate-spin text-primary" /></div>;
  if (isBilling) {
    if (access.isError || !access.data || access.data.locked) return <>{recoveryChildren ?? children}</>;
    return <>{children}</>;
  }
  if (access.isError || !access.data) return <div role="alert" data-contrast="dark" className="premium-dark-surface min-h-[100dvh] bg-[#0e0e0e] px-6 py-14 text-center text-white"><h1 className="text-2xl font-bold">We could not check your access</h1><p className="mt-3">Check your connection and try again. You do not need to buy another membership.</p><button className="mt-6 min-h-11 rounded-xl bg-primary px-6 py-3 text-black" onClick={() => void access.refetch()}>Try again</button><button className="ml-3 mt-6 min-h-11 rounded-xl border border-white/20 px-6 py-3" onClick={() => navigate('/client/billing')}>Review membership</button></div>;

  // An inactive client cannot regain training or enrollment access through an
  // active subscription, a scoped pass, or a direct activation URL.
  if (access.data.reason === 'inactive') return <AccountAccessPaused reason="inactive" navigate={navigate} />;
  if (isEnrollment && (!access.data.locked || access.data.enrollmentPending)) return <>{access.data.locked ? recoveryChildren ?? children : children}</>;
  if (bootstrapping || (access.data.enrollmentPending && !bootstrapAttempted.current && !isEnrollment)) return <div role="status" data-contrast="dark" className="premium-dark-surface flex min-h-[100dvh] items-center justify-center gap-3 bg-[#0e0e0e] text-white"><Loader2 className="size-5 animate-spin" />Checking your membership setup</div>;
  if (access.data.locked) return <AccountAccessPaused reason="unpaid" navigate={navigate} setupPath={access.data.enrollmentPending ? setupPath : undefined} />;
  return <ClientSubscriptionCheck navigate={navigate}>{children}</ClientSubscriptionCheck>;
}

function ClientSubscriptionCheck({
  children,
  navigate,
}: {
  children: React.ReactNode;
  navigate: (path: string) => void;
}) {
  const { data: subscription, isLoading: subLoading, isError: subError, refetch: retrySubscription } = trpc.billing.getSubscription.useQuery(undefined, {
    retry: false,
  });
  const { data: standardState, isLoading: standardLoading, isError: standardError, refetch: retryStandard } = trpc.standardMembership.getMyState.useQuery(undefined, { retry: false });
  const [location] = useLocation();
  const { data: scoped, isLoading: scopedLoading, isError: scopedError, refetch: retryScoped } = trpc.community.getMyScopedAccess.useQuery(undefined, { retry: false });

  if (subLoading || scopedLoading || standardLoading) {
    return (
      <div data-contrast="dark" className="premium-dark-surface flex items-center justify-center min-h-[60dvh]" style={{ background: '#0e0e0e' }}>
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--primary)' }} />
      </div>
    );
  }

  if (subError || standardError || scopedError) {
    return <div role="alert" data-contrast="dark" className="premium-dark-surface min-h-[60dvh] bg-[#0e0e0e] px-6 py-14 text-center text-white"><h1 className="text-2xl font-bold">We could not check your access</h1><p className="mt-3">Check your connection and try again. You do not need to buy another membership.</p><button className="mt-6 rounded-xl bg-primary px-6 py-3 text-black" onClick={() => { void retrySubscription(); void retryStandard(); void retryScoped(); }}>Try again</button><button className="ml-3 mt-6 rounded-xl border border-white/20 px-6 py-3" onClick={() => navigate('/client/billing')}>Review membership</button></div>;
  }

  const hasActiveSub = subscription?.status === "active" || subscription?.status === "trialing";

  if (!hasActiveSub && standardState && !standardState.hasAccess && standardState.canActivate) {
    return <Redirect to="/client/standard-activation" />;
  }

  if (standardState?.hasAccess && !standardState.entitlement.firstLoginCompletedAt && (!hasActiveSub || ('accessSource' in (subscription ?? {})))) {
    return <Redirect to="/client/standard-start" />;
  }

  if (standardState?.selfDirectedOnly && (location.startsWith('/client/comms') || location.startsWith('/client/form-check'))) {
    return (
      <div data-contrast="dark" className="premium-dark-surface min-h-[70dvh] bg-[#0e0e0e] px-6 py-14 text-white">
        <div className="mx-auto max-w-lg rounded-2xl border border-white/10 bg-white/[0.03] p-7 text-center">
          <Shield className="mx-auto h-10 w-10 text-primary" />
          <h1 className="mt-5 text-3xl font-black uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Self-Directed Membership</h1>
          <p className="mt-3 text-sm leading-6 text-zinc-400">One-to-one coaching messages and form reviews are not included in this membership. Your training, progress tools, resources, and Standard Community remain available.</p>
          <button onClick={() => navigate('/client')} className="mt-7 rounded-xl bg-primary px-6 py-3 text-xs font-black uppercase tracking-widest text-black">Go to today</button>
        </div>
      </div>
    );
  }

  if (!hasActiveSub && scoped?.active) {
    const groupMatch = location.match(/^\/community\/groups\/(\d+)/);
    const challengeMatch = location.match(/^\/client\/challenges\/(\d+)/);
    const allowed = location === '/client'
      || location.startsWith('/client/account')
      || location.startsWith('/client/billing')
      || location.startsWith('/client/community')
      || (groupMatch && scoped.groupIds.includes(Number(groupMatch[1])))
      || (challengeMatch && scoped.challengeIds.includes(Number(challengeMatch[1])))
      || (scoped.journalAccess && location.startsWith('/client/journal'))
      || (scoped.programIds.length > 0 && (location.startsWith('/client/programs') || location.startsWith('/client/training')));
    if (allowed) {
      if (location === '/client') {
        const firstGrant = scoped.grants[0];
        return (
          <div data-contrast="dark" className="premium-dark-surface min-h-[70dvh] bg-[#0e0e0e] px-5 py-10 text-white">
            <div className="mx-auto max-w-xl">
              <p className="text-[10px] font-black uppercase tracking-[0.3em] text-primary">Your Access Pass</p>
              <h1 className="mt-2 text-4xl font-black uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Your work is ready</h1>
              <p className="mt-2 text-sm text-zinc-400">Your invitation gives you focused access through {new Date(firstGrant.expiresAt).toLocaleDateString()}.</p>
              <div className="mt-8 grid gap-3">
                <button onClick={() => navigate(`/community/groups/${firstGrant.groupId}`)} className="flex items-center gap-4 rounded-xl border border-white/10 bg-white/[0.04] p-5 text-left"><Users className="h-6 w-6 text-primary" /><span className="font-black uppercase">Open your group</span><ArrowRight className="ml-auto h-4 w-4" /></button>
                {scoped.programIds.length > 0 ? <button onClick={() => navigate('/client/programs')} className="flex items-center gap-4 rounded-xl border border-white/10 bg-white/[0.04] p-5 text-left"><Dumbbell className="h-6 w-6 text-primary" /><span className="font-black uppercase">Open your program</span><ArrowRight className="ml-auto h-4 w-4" /></button> : null}
                {scoped.journalAccess ? <button onClick={() => navigate('/client/journal')} className="flex items-center gap-4 rounded-xl border border-white/10 bg-white/[0.04] p-5 text-left"><BookOpen className="h-6 w-6 text-primary" /><span className="font-black uppercase">Open your journal</span><ArrowRight className="ml-auto h-4 w-4" /></button> : null}
              </div>
            </div>
          </div>
        );
      }
      return <>{children}</>;
    }
  }

  if (!hasActiveSub) return <AccountAccessPaused reason="unpaid" navigate={navigate} />;

  return <>{children}</>;
}

function AccountAccessPaused({ reason, navigate, setupPath }: { reason: 'inactive' | 'unpaid'; navigate: (path: string) => void; setupPath?: string }) {
  const isNativeApp = Capacitor.isNativePlatform();
  const logout = trpc.auth.logout.useMutation({
    retry: false,
    onSettled: () => { setNativeSessionToken(null); window.location.href = '/login'; },
  });
  const signOut = () => { if (!logout.isPending) logout.mutate(); };
  const [search, setSearch] = useSearchParams();
  const showSupport = search.get('accountSupport') === '1';
  // A paused client sees the coach's latest note, the reason they were paused.
  const thread = trpc.accountSupport.thread.useQuery(undefined, { enabled: reason === 'inactive' && !showSupport, retry: false });
  const coachNote = reason === 'inactive' ? [...(thread.data?.messages ?? [])].reverse().find((message) => message.fromCoach) : undefined;
  const closeSupport = () => setSearch(previous => { const next = new URLSearchParams(previous); next.delete('accountSupport'); return next; }, { replace: true });
  useEffect(() => {
    if (!isNativeApp || !showSupport) return;
    let disposed = false;
    let remove: (() => Promise<void>) | undefined;
    void import('@capacitor/app').then(({ App }) => App.addListener('backButton', closeSupport)).then(handle => {
      if (disposed) void handle.remove();
      else remove = () => handle.remove();
    }).catch(() => { /* Native back support is best-effort. */ });
    return () => { disposed = true; if (remove) void remove(); };
    // The search parameters are the source of truth for browser Back/Forward.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNativeApp, showSupport, search.toString()]);
  if (showSupport) return <div className="mx-auto h-[100dvh] max-w-3xl"><AccountSupportThread onBack={closeSupport} onReviewMembership={() => navigate('/client/billing')} onSignOut={signOut} signingOut={logout.isPending} /></div>;

  return (
      <div data-contrast="dark" className="premium-dark-surface flex flex-col items-center justify-center min-h-[100dvh] px-6 py-10 text-center"
        style={{ background: '#0e0e0e' }}>
        {/* Lock icon */}
        <div className="w-20 h-20 rounded-xl flex items-center justify-center mb-6"
          style={{ background: 'color-mix(in oklch, var(--primary) 8%, transparent)', border: '1px solid rgba(245,158,11,0.2)' }}>
          <Lock className="w-9 h-9" style={{ color: 'var(--primary)' }} strokeWidth={1.5} />
        </div>

        {/* Brand label */}
        <div className="flex items-center gap-2 mb-4">
          <Shield className="w-4 h-4" style={{ color: 'var(--primary)' }} strokeWidth={1.5} />
          <span className="text-[9px] font-black uppercase tracking-[0.35em]"
            style={{ color: 'var(--primary)', fontFamily: 'Barlow Condensed, sans-serif' }}>
            The Standard
          </span>
        </div>

        {/* Heading */}
        <h2 className="text-3xl font-black uppercase leading-tight mb-3 max-w-xs"
          style={{ fontFamily: 'Barlow Condensed, sans-serif', color: '#fff', letterSpacing: '0.03em' }}>
          {reason === 'inactive' ? 'Your Access Is Paused' : 'Access Requires a Plan'}
        </h2>

        {coachNote ? (
          <div className="mb-5 w-full max-w-sm rounded-2xl border border-white/10 bg-white/[0.04] p-4 text-left" data-testid="paused-coach-note">
            <p className="text-xs font-bold uppercase tracking-widest text-zinc-400">From Coach Fe</p>
            <p className="mt-2 whitespace-pre-line text-[15px] leading-relaxed text-zinc-100">{coachNote.content}</p>
          </div>
        ) : null}

        {/* Body */}
        <p className="text-sm max-w-sm mb-8 leading-relaxed text-zinc-400">
          {reason === 'inactive'
            ? 'Your workouts, progress and history are saved. Message Coach Fe and once you two sort it out, everything comes back right where you left it.'
            : isNativeApp
            ? 'This account does not have active client access. Contact The Standard support team or your coach for help.'
            : 'Your account is set up. Pick a plan to unlock programs, nutrition, check-ins, and messages.'}
        </p>

        {/* CTA: a paused client has one way forward, talking to Coach Fe. */}
        {reason !== 'inactive' && <button
          onClick={() => navigate("/client/billing")}
          className="flex items-center gap-2 px-8 py-4 rounded-xl text-sm font-black uppercase tracking-widest transition-all active:opacity-80"
          style={{ background: 'linear-gradient(135deg, var(--primary), color-mix(in oklch, var(--primary) 75%, black))', color: '#000', fontFamily: 'Barlow Condensed, sans-serif' }}
        >
          {isNativeApp ? 'Review Membership' : 'Choose Your Plan'} <ArrowRight className="w-4 h-4" />
        </button>}

        {setupPath && <button type="button" onClick={() => navigate(setupPath)} className="mt-4 min-h-11 rounded-xl border border-white/25 px-6 py-3 text-sm font-bold text-white">Continue account setup</button>}
        <button type="button" onClick={() => setSearch(previous => { const next = new URLSearchParams(previous); next.set('accountSupport', '1'); return next; })} className={reason === 'inactive'
          ? 'flex min-h-12 items-center gap-2 rounded-xl px-8 py-4 text-sm font-black uppercase tracking-widest text-black'
          : 'mt-4 flex min-h-11 items-center gap-2 rounded-xl border border-white/25 px-6 py-3 text-sm font-bold text-white'}
          style={reason === 'inactive' ? { background: 'var(--primary)', fontFamily: 'Barlow Condensed, sans-serif' } : undefined}>
          <MessageSquare className="size-4" aria-hidden="true" />Contact Coach Fe
        </button>
        {reason !== 'inactive' && <p className="mt-3 max-w-sm text-xs leading-5 text-zinc-400">Account support remains available. Your app access stays paused until your account is restored.</p>}

        <button type="button" onClick={signOut} disabled={logout.isPending} className="mt-4 min-h-11 rounded-lg px-5 py-2 text-sm text-zinc-400 underline underline-offset-4 disabled:opacity-50">{logout.isPending ? 'Signing out…' : 'Sign out'}</button>

        {!isNativeApp && reason !== 'inactive' && <p className="mt-4 text-[9px] font-mono uppercase tracking-widest text-zinc-400">
          Cancel anytime. No contracts.
        </p>}
      </div>
    );
}
